import { z } from 'zod';
import { ColorSchema, NameSchema, PRIMARY_SOURCE, about, type SlotNeeds } from './schema_fields.ts';
import { ActionOptionsSchema } from './tiles/action.ts';
import { AirQualityOptionsSchema } from './tiles/air_quality.ts';
import { ForecastOptionsSchema } from './tiles/forecast.ts';
import { ImageOptionsSchema } from './tiles/image.ts';
import { TextOptionsSchema } from './tiles/text.ts';
import { GraphOptionsSchema, GraphSchema } from './tiles/graph.ts';
import { LightOptionsSchema } from './tiles/light.ts';
import { SolarOptionsSchema } from './tiles/solar.ts';
import { StatusOptionsSchema } from './tiles/status.ts';
import { SwitchOptionsSchema } from './tiles/switch.ts';
import { TableOptionsSchema } from './tiles/table.ts';
import { ThermostatOptionsSchema } from './tiles/thermostat.ts';
import { ValueOptionsSchema } from './tiles/value.ts';
import { VideoOptionsSchema } from './tiles/video.ts';
import { STYLE_SHAPES, TileStyleSchema } from './tile_style.ts';
import { TileTapSchema } from './tile_tap.ts';

/**
 * The dashboard file (proposal §6.2, clean slate: no legacy import, decision §2). One file holds the
 * connections with their credentials (decision §12) and the screens with their tiles. Tiles name
 * their data sources; options refer to those names. Cross-references are checked by
 * `parseDashboard`, not here.
 *
 * This file holds what the whole file and every tile have; each tile type's options are in
 * `tiles/<type>.ts`, and the parts they share in `schema_fields.ts`. How to change the schema or
 * add a tile type: docs/dashboard-schema.md.
 */

export { ColorSchema, ColorsSchema, ConditionSchema, NameSchema, NumberFormatSchema, PRIMARY_SOURCE, TransformSchema, type SlotNeeds } from './schema_fields.ts';
export { ActionOptionsSchema } from './tiles/action.ts';
export { AirQualityOptionsSchema } from './tiles/air_quality.ts';
export { IMAGE_FITS, ImageOptionsSchema } from './tiles/image.ts';
export { FORECAST_HORIZONS, ForecastOptionsSchema, type ForecastHorizon, type ForecastThreshold } from './tiles/forecast.ts';
export { TEXT_ALIGNMENTS, TEXT_VERTICAL_ALIGNMENTS, TextOptionsSchema } from './tiles/text.ts';
export { GRAPH_FILLS, GraphOptionsSchema, GraphSchema, type GraphFill } from './tiles/graph.ts';
export { LightOptionsSchema } from './tiles/light.ts';
export { SOLAR_DESIGNS, SolarOptionsSchema } from './tiles/solar.ts';
export { StatusOptionsSchema, StatusStateSchema, type StatusState } from './tiles/status.ts';
export { SwitchOptionsSchema } from './tiles/switch.ts';
export { TableCellSchema, TableOptionsSchema } from './tiles/table.ts';
export { ThermostatOptionsSchema } from './tiles/thermostat.ts';
export { SPARKLINE_PLACEMENTS, UNIT_POSITIONS, ValueOptionsSchema, type SparklinePlacement, type UnitPosition } from './tiles/value.ts';
export { VideoOptionsSchema } from './tiles/video.ts';
export { CommandTapSchema, ScreenTapSchema, TAP_SOURCE, TileTapSchema, commandTapJsonSchema, hasOwnTap, tapOf, type TileTap, type TileTapKind } from './tile_tap.ts';

export const SCHEMA_VERSION = 4;

/** A driver instance: the legacy "data service". `config` is the driver's, credentials included. */
export const ConnectionSchema = z.object({
    driver: z.string().min(1).describe('the built-in driver, for example influx or anna'),
    config: z.record(z.string(), z.unknown()).describe('the driver settings; credential fields are marked in the driver config schema'),
});

/** One data source of a tile: which connection, and what the driver should read for it. */
export const DataSourceSchema = z.object({
    connection: NameSchema,
    /** Seconds between refreshes; the driver's own interval when absent. */
    refreshInterval: z.number().positive().optional(),
    /** Driver-specific: `attribute`, `tag`/`tagValue`, `query`, `measurement`/`field`/`func`/`period`. */
    params: z.record(z.string(), z.unknown()).default({}),
});

export const LayoutSchema = z.object({
    /** 1-based grid column and row. */
    x: z.number().int().min(1),
    y: z.number().int().min(1),
    width: z.number().int().min(1),
    height: z.number().int().min(1),
});

export { ALIGNMENT_LABELS, ALIGNMENTS, GAUGE_STYLES, GaugeStyleSchema, ICON_POSITION_LABELS, ICON_POSITIONS, NONE, nearestStyleShape, STYLE_SHAPE_RATIOS, STYLE_SHAPES, TileStyleSchema, type Alignment, type GaugeStyle, type IconPosition, type StyleShape, type TileStyle } from './tile_style.ts';
export { GaugeLookSchema, ThresholdSchema, type GaugeLook, type Threshold } from './tiles/gauge_look.ts';

const tileBase = {
    /** Unique in the dashboard. Derived from the tile's place in the file when absent (`screen1-tile3`). */
    id: NameSchema.optional(),
    label: z.string().optional(),
    /** An icon at the right of the title (legacy `titleIcon`), by its legacy or Font Awesome name. */
    titleIcon: z.string().optional(),
    layout: LayoutSchema,
    /** The tile's one data source: short for `dataSources.primary`. */
    dataSource: DataSourceSchema.optional(),
    /** The tile's data sources by name, for tiles with more than one. */
    dataSources: z.record(NameSchema, DataSourceSchema).default({}),
    style: TileStyleSchema.optional(),
    /** The style picked for it, by name, only to show it (tile-styles.md TY-8): the tile is drawn from its own style and options. */
    styleName: z.string().optional(),
    /** What a tap does, where the type has no tap of its own (tiles spec §6): go to a screen, or send a command. */
    tap: TileTapSchema.optional(),
};

/**
 * Tiles as a file may write them: `options` may be left out where every option has a default, and
 * one data source may be given as `dataSource`.
 */
const tileInputs = [
    z.object({ type: z.literal('value'), ...tileBase, options: ValueOptionsSchema.prefault({}) }),
    z.object({ type: z.literal('graph'), ...tileBase, options: GraphOptionsSchema }),
    z.object({ type: z.literal('solar'), ...tileBase, options: SolarOptionsSchema.prefault({}) }),
    z.object({ type: z.literal('thermostat'), ...tileBase, options: ThermostatOptionsSchema }),
    z.object({ type: z.literal('airquality'), ...tileBase, options: AirQualityOptionsSchema.prefault({}) }),
    z.object({ type: z.literal('video'), ...tileBase, options: VideoOptionsSchema }),
    z.object({ type: z.literal('image'), ...tileBase, options: ImageOptionsSchema.prefault({}) }),
    z.object({ type: z.literal('light'), ...tileBase, options: LightOptionsSchema.prefault({}) }),
    z.object({ type: z.literal('switch'), ...tileBase, options: SwitchOptionsSchema.prefault({}) }),
    z.object({ type: z.literal('action'), ...tileBase, options: ActionOptionsSchema }),
    z.object({ type: z.literal('table'), ...tileBase, options: TableOptionsSchema }),
    z.object({ type: z.literal('status'), ...tileBase, options: StatusOptionsSchema.prefault({}) }),
    z.object({ type: z.literal('forecast'), ...tileBase, options: ForecastOptionsSchema.prefault({}) }),
    z.object({ type: z.literal('text'), ...tileBase, options: TextOptionsSchema.prefault({}) }),
] as const;

/** Each tile type's options, as the file writes them (for the editor's forms and slots, T-4). */
export const TILE_OPTIONS_SCHEMAS = {
    value: ValueOptionsSchema,
    graph: GraphOptionsSchema,
    solar: SolarOptionsSchema,
    thermostat: ThermostatOptionsSchema,
    airquality: AirQualityOptionsSchema,
    video: VideoOptionsSchema,
    image: ImageOptionsSchema,
    light: LightOptionsSchema,
    switch: SwitchOptionsSchema,
    action: ActionOptionsSchema,
    table: TableOptionsSchema,
    status: StatusOptionsSchema,
    forecast: ForecastOptionsSchema,
    text: TextOptionsSchema,
} as const;

const OPTIONS_TYPES = Object.keys(TILE_OPTIONS_SCHEMAS) as (keyof typeof TILE_OPTIONS_SCHEMAS)[];

/**
 * A style to pick for a tile of one type (tile-styles.md TY-1): a tile style, and values for the
 * look options (TY-2) of its type, as they nest (`{ "unitSize": 80, "sparkline": { "color":
 * "#38bdf8" } }`). Only tiles of its type are offered it. The user's own are kept in the
 * dashboard (`tileStyles`, TY-9).
 */
export const StylePresetSchema = z.object({
    name: z.string().min(1),
    type: z.enum(OPTIONS_TYPES).meta(about('Tile type', 'the type of tile it is for')),
    shape: z.enum(STYLE_SHAPES).optional().meta(about('Shape', 'the tile shape it is made for; left out: any')),
    style: TileStyleSchema.default({}),
    options: z.record(z.string(), z.unknown()).default({}),
});

export type StylePreset = z.infer<typeof StylePresetSchema>;

/** A slot of a tile type: where in its options the source's name goes, and what it needs. */
export interface TileSlot extends SlotNeeds {
    /** The option's path: `['source']`, later `['temperature', 'source']`. */
    readonly path: readonly string[];
}

const optionsJsonSchemas = new Map<string, Record<string, unknown>>();

/** A tile type's options as JSON Schema, as the file writes them (defaults left out), for the editor's form (§5.7). */
export function tileOptionsJsonSchema(type: keyof typeof TILE_OPTIONS_SCHEMAS) : Record<string, unknown> {
    let schema = optionsJsonSchemas.get(type);
    if (!schema) {
        schema = z.toJSONSchema(TILE_OPTIONS_SCHEMAS[type], { io: 'input', unrepresentable: 'any' }) as Record<string, unknown>;
        optionsJsonSchemas.set(type, schema);
    }

    return schema;
}

/** The slots of a tile type, from the hints in its options' schema; those of each of its ways too (a thermostat's, F-3). */
export function tileSlots(type: keyof typeof TILE_OPTIONS_SCHEMAS) : TileSlot[] {
    const slots: TileSlot[] = [];
    const walk = (schema: unknown, path: string[]) : void => {
        if (typeof schema !== 'object' || schema === null) {
            return;
        }

        const node = schema as { slot?: SlotNeeds; properties?: Record<string, unknown>; anyOf?: unknown[]; oneOf?: unknown[] };
        if (node.slot) {
            slots.push({ ...node.slot, path });
        }
        for (const [name, property] of Object.entries(node.properties ?? {})) {
            walk(property, [...path, name]);
        }
        for (const variant of [...(node.anyOf ?? []), ...(node.oneOf ?? [])]) {
            walk(variant, path);
        }
    };
    walk(tileOptionsJsonSchema(type), []);

    return slots;
}

/**
 * The sources a tile's options name, with where (`options.graph.series[0].source`), through their
 * slots at any depth: list items, sub-objects and the object variant of a union (a table cell that
 * reads). From the slots in the type's schema, so a new tile type needs no list of its own.
 */
export function sourceReferences(type: keyof typeof TILE_OPTIONS_SCHEMAS, options: unknown) : [where: string, name: string][] {
    const references: [string, string][] = [];
    const isObject = (value: unknown) : value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
    const walk = (schema: unknown, value: unknown, where: string) : void => {
        if (!isObject(schema)) {
            return;
        }
        if (schema.slot !== undefined && typeof value === 'string') {
            references.push([where, value]);

            return;
        }
        if (Array.isArray(value)) {
            for (const [index, item] of value.entries()) {
                walk(schema.items, item, `${where}[${index}]`);
            }

            return;
        }
        if (!isObject(value)) {
            return;
        }

        const variants = [schema, ...(Array.isArray(schema.anyOf) ? schema.anyOf : []), ...(Array.isArray(schema.oneOf) ? schema.oneOf : [])];
        for (const variant of variants) {
            if (isObject(variant) && isObject(variant.properties)) {
                for (const [key, property] of Object.entries(variant.properties)) {
                    walk(property, value[key], `${where}.${key}`);
                }
            }
        }
    };
    walk(tileOptionsJsonSchema(type), options, 'options');

    return references;
}

/** The names of the sources a tile's options name (`sourceReferences`), for removing a source an edit left unused (§5.7). */
export function sourcesNamed(type: keyof typeof TILE_OPTIONS_SCHEMAS, options: unknown) : Set<string> {
    return new Set(sourceReferences(type, options).map(([, name]) => name));
}

/** A tile once parsed: every data source in `dataSources`, the short form folded in. */
type Distribute<T> = T extends unknown ? Omit<T, 'dataSource'> : never;

/** Folds the short `dataSource` into `dataSources.primary`. */
function foldDataSource<T extends { dataSource?: z.output<typeof DataSourceSchema> | undefined; dataSources: Record<string, z.output<typeof DataSourceSchema>> }>(
    tile: T,
    context: z.RefinementCtx,
) : Distribute<T> {
    const { dataSource, ...rest } = tile;
    if (dataSource === undefined) {
        return rest as Distribute<T>;
    }
    if (Object.hasOwn(rest.dataSources, PRIMARY_SOURCE)) {
        context.addIssue({ code: 'custom', path: ['dataSource'], message: `dataSource is short for dataSources.${PRIMARY_SOURCE}; give one of them, not both` });

        return z.NEVER;
    }

    return { ...rest, dataSources: { [PRIMARY_SOURCE]: dataSource, ...rest.dataSources } } as unknown as Distribute<T>;
}

/** A tile other than a group; after parsing, its short `dataSource` is `dataSources.primary`. */
export const TileSchema = z.discriminatedUnion('type', [...tileInputs]).transform((tile, context) => foldDataSource(tile, context));

/**
 * A group (legacy `GroupTile`): an empty box with a title, drawn behind every other tile of its
 * screen, so that it can be laid under tiles that belong together (the owner, 2026-10-16: holding
 * tiles in it, as Phase 2 T7 built it, did not work out in the editor). It holds no tiles. A file
 * whose group still holds tiles (as T7 wrote them, placed on the group's own grid of as many
 * columns and rows as the group is wide and high) is read with those tiles on the screen at the
 * same places, right after the group.
 */
const GroupInputSchema = z.object({
    type: z.literal('group'),
    id: NameSchema.optional(),
    label: z.string().optional(),
    titleIcon: z.string().optional(),
    layout: LayoutSchema,
    style: TileStyleSchema.optional(),
    styleName: z.string().optional(),
    /** Read, not written: tiles in the group as Phase 2 T7 had them; they are moved onto the screen. */
    tiles: z.array(TileSchema).optional(),
});

/** A tile on a screen: a tile, or a group. */
export const ScreenTileSchema = z.discriminatedUnion('type', [...tileInputs, GroupInputSchema]).transform((tile, context) =>
    tile.type === 'group' ? tile : foldDataSource(tile, context),
);

/**
 * The grid of the whole dashboard (owner, 2026-10-15): `columns` across the width, and `rows`
 * either a number, so the grid fills the screen, or not set, so the grid is as tall as its tiles
 * need and the screen scrolls vertically, a row as high as a column is wide. Nothing about the
 * kind of device. A dashboard without a grid keeps the legacy 48 × 48. `margin` (from the screen's
 * edges) and `spacing` (between cells) are the app's 4 and 6 pixels where left out (issue #35, DS-4).
 */
export const GridSchema = z
    .object({
        columns: z.number().int().min(1).optional(),
        rows: z.number().int().min(1).optional(),
        margin: z.number().min(0).optional(),
        spacing: z.number().min(0).optional(),
    })
    .transform(({ columns, rows, margin, spacing }) : { columns: number; rows?: number; margin?: number; spacing?: number } => ({
        ...(rows === undefined ? { columns: columns ?? 4 } : { columns: columns ?? 48, rows }),
        ...(margin === undefined ? {} : { margin }),
        ...(spacing === undefined ? {} : { spacing }),
    }));

export const LEGACY_GRID = { columns: 48, rows: 48 } as const;

export const ScreenSchema = z.object({
    name: z.string().min(1),
    icon: z.string().optional(),
    /** Not here any more: every screen of a dashboard has the dashboard's grid. */
    grid: z.never({ error: 'the grid is set for the whole dashboard now ("grid" next to "screens")' }).optional(),
    tiles: z.array(ScreenTileSchema),
});

/**
 * The id of a tile that gives none: its place in the file. Stable while the file keeps its order;
 * the editor (Phase 3) writes ids into the file.
 */
function derivedTileId(screenIndex: number, tileIndex: number, taken: ReadonlySet<string>) : string {
    return uniqueId(`screen${screenIndex + 1}-tile${tileIndex + 1}`, taken);
}

/** `base`, or `base-2`, `base-3`, … when it is taken. */
function uniqueId(base: string, taken: ReadonlySet<string>) : string {
    let id = base;
    for (let n = 2; taken.has(id); n++) {
        id = `${base}-${n}`;
    }

    return id;
}

/** The dashboard; once parsed, every tile has an `id`. */
export const DashboardSchema = z
    .object({
        schemaVersion: z.literal(SCHEMA_VERSION),
        connections: z.record(NameSchema, ConnectionSchema).default({}),
        grid: GridSchema.default(LEGACY_GRID),
        /** Behind every screen's tiles (issue #35, DS-3); black where left out. Not a tile's. */
        background: ColorSchema.optional(),
        /** The tiles' style where a tile sets none of its own (TS-2); left out, the defaults. */
        tileStyle: TileStyleSchema.optional(),
        /** The user's own styles to pick for a tile (tile-styles.md TY-9), after the built-in ones. */
        tileStyles: z.array(StylePresetSchema).optional(),
        screens: z.array(ScreenSchema).min(1),
    })
    .transform((dashboard, context) => {
        const taken = new Set<string>();
        const claim = (id: string | undefined, path: (string | number)[]) : void => {
            if (id === undefined) {
                return;
            }
            if (taken.has(id)) {
                context.addIssue({ code: 'custom', path: [...path, 'id'], message: `another tile has the id "${id}"` });
            }
            taken.add(id);
        };
        for (const [screenIndex, screen] of dashboard.screens.entries()) {
            for (const [tileIndex, tile] of screen.tiles.entries()) {
                claim(tile.id, ['screens', screenIndex, 'tiles', tileIndex]);
                if (tile.type === 'group') {
                    for (const [childIndex, child] of (tile.tiles ?? []).entries()) {
                        claim(child.id, ['screens', screenIndex, 'tiles', tileIndex, 'tiles', childIndex]);
                    }
                }
            }
        }

        const withId = <T extends { id?: string | undefined }>(tile: T, derived: () => string) : T & { id: string } => {
            if (tile.id !== undefined) {
                return { ...tile, id: tile.id };
            }

            const id = derived();
            taken.add(id);

            return { ...tile, id };
        };

        return {
            ...dashboard,
            screens: dashboard.screens.map((screen, screenIndex) => ({
                ...screen,
                tiles: screen.tiles.flatMap((tile, tileIndex) => {
                    if (tile.type !== 'group') {
                        return [withId(tile, () => derivedTileId(screenIndex, tileIndex, taken))];
                    }

                    const { tiles: held = [], ...box } = withId(tile, () => derivedTileId(screenIndex, tileIndex, taken));
                    // Tiles a group held (T7) onto the screen, where they were: the group's own grid starts at its top left.
                    const moved = held.map((child, childIndex) => {
                        const placed = withId(child, () => uniqueId(`${box.id}-tile${childIndex + 1}`, taken));

                        return { ...placed, layout: { ...placed.layout, x: box.layout.x + placed.layout.x - 1, y: box.layout.y + placed.layout.y - 1 } };
                    });

                    return [box, ...moved];
                }),
            })),
        };
    });

export type Dashboard = z.infer<typeof DashboardSchema>;
export type Screen = Dashboard['screens'][number];
export type Grid = Dashboard['grid'];
/** What a screen holds: tiles, and groups (boxes with a title). */
export type ScreenTile = Screen['tiles'][number];
export type GroupTile = Extract<ScreenTile, { type: 'group' }>;
/** A tile that shows something: anything on a screen but a group. */
export type Tile = Exclude<ScreenTile, { type: 'group' }>;

/** The tiles of a screen that show something (not its groups). */
export function tilesOf(screen: Screen) : Tile[] {
    return screen.tiles.filter((tile): tile is Tile => tile.type !== 'group');
}
export type TileType = Tile['type'];
export type TileOf<Type extends TileType> = Extract<Tile, { type: Type }>;
export type Connection = z.infer<typeof ConnectionSchema>;
export type DataSource = z.infer<typeof DataSourceSchema>;
export type Graph = z.infer<typeof GraphSchema>;
export type ValueOptions = z.infer<typeof ValueOptionsSchema>;
